import { FC, useRef, useEffect } from "react";

import { useForm } from "react-hook-form";

import { useCurrentUserContext } from "../../../../../../common/contexts/CurrentUserContext";
import { trpc } from "../../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "../../useFormSaveAndNavigate";
import { getTicketingDescriptionStepWording, prefillDescription } from "../shared/helper";

import { LiveEditorStepDescriptionStateless } from "./LiveEditorStepDescription.stateless";

import { useArrow } from "@/components/LiveEditorElements/AnimatePreview/Arrow/ArrowContext";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export const TicketingLiveEditorStepDescription: FC = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const formCategory = apiTicketing.formCategory;
  const isSweepstakes = isSweepstakesCategory(apiTicketing.formCategory);
  const { refreshArrowPosition, hideArrow, showArrow } = useArrow();
  const prefilledText = isSweepstakes
    ? `<p>${t("dashboard", "sweepstakes.description.default", { organizationName: organization?.name ?? "" })}</p>`
    : prefillDescription(t, organization?.name, apiTicketing.formCategory);

  const localizedTicketingField = apiTicketing.ticketingFields?.find((field) => field.locale === apiTicketing.locale);
  const initialDescription = localizedTicketingField?.description || null;

  const defaultValues: StepDescriptionFormData = {
    description: initialDescription,
  };
  const form = useForm<StepDescriptionFormData>({ defaultValues });

  const updateTicketingDescription = trpc.updateTicketingDescription.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDescriptionFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      return await updateTicketingDescription.mutateAsync({
        ticketingId: apiTicketing.id,
        description: formData.description,
      });
    },
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const descriptionInPreview = formData.description || prefilledText;
      liveDataToMutate.ticketing.ticketingFields![0].description = descriptionInPreview;
      liveDataToMutate.ticketing.ticketingFields![0].sanitizedDescription = descriptionInPreview;
      liveDataToMutate.placeholders.isDescription = !formData.description;
    },
  });

  const description = form.watch("description");
  // When the description is toggled from null <-> non-null, hide the arrow and show it after a delay to avoid arrow flickering
  // This is needed because there is a lot of update of the DOM when the description is toggled
  const prevDescriptionRef = useRef(description);
  useEffect(() => {
    const wasNull = prevDescriptionRef.current === null;
    const isNull = description === null;

    // If toggled from null->defined or defined->null
    if (wasNull !== isNull) {
      hideArrow();
      setTimeout(() => {
        refreshArrowPosition();
        showArrow();
      }, 800);
    }

    prevDescriptionRef.current = description;
  }, [description, hideArrow, refreshArrowPosition, showArrow]);

  return (
    <LiveEditorStepDescriptionStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      initialDescription={initialDescription}
      prefilledText={prefilledText}
      wording={{
        stepTitle: isSweepstakes
          ? t("dashboard", "sweepstakes.description.title")
          : getTicketingDescriptionStepWording(t, formCategory).stepTitle,
        stepDescription: isSweepstakes
          ? t("dashboard", "sweepstakes.description.description")
          : getTicketingDescriptionStepWording(t, formCategory).stepDescription,
      }}
      forbiddenWordMessage={isSweepstakes ? t("dashboard", "sweepstakes.validation.raffleWord.description") : undefined}
    />
  );
};
